<template>
  <div class="gl-loyalty-page">

    <!-- Page header -->
    <div class="container-fluid">
      <div class="gl-page-header">
        <div>
          <div class="gl-breadcrumb">
            <router-link to="/">Home</router-link>
            <span class="gl-bc-sep">/</span>
            <span>Loyalty</span>
          </div>
          <h1 class="gl-page-title">Loyalty Programme</h1>
        </div>
      </div>
    </div>

    <div class="container-fluid">

      <!-- Skeleton -->
      <template v-if="loading">
        <div class="gl-skel-page">
          <div class="gl-skel-card-block" style="height:200px"></div>
          <div class="gl-skel-card-block" style="height:260px"></div>
          <div class="gl-skel-card-block" style="height:300px"></div>
        </div>
      </template>

      <template v-else>

        <!-- ① How it works -->
        <div class="gl-section-card">
          <div class="gl-section-head">
            <div class="gl-section-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"/></svg>
            </div>
            <div>
              <h2 class="gl-section-title">How the Loyalty Programme Works</h2>
              <p class="gl-section-sub">Reward your customers every time they order. Points are earned per ringgit spent, multiplied by the customer's tier, and can be redeemed as a discount on future orders.</p>
            </div>
          </div>

          <!-- Flow pipeline -->
          <div class="gl-flow">
            <div class="gl-flow-step">
              <div class="gl-flow-icon gl-flow-blue">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>
              </div>
              <span class="gl-flow-label">Customer orders</span>
            </div>
            <div class="gl-flow-arrow">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
            </div>
            <div class="gl-flow-step">
              <div class="gl-flow-icon gl-flow-teal">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
              </div>
              <span class="gl-flow-label">Earns points</span>
            </div>
            <div class="gl-flow-arrow">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
            </div>
            <div class="gl-flow-step">
              <div class="gl-flow-icon gl-flow-amber">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
              </div>
              <span class="gl-flow-label">Tier multiplier applied</span>
            </div>
            <div class="gl-flow-arrow">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
            </div>
            <div class="gl-flow-step">
              <div class="gl-flow-icon gl-flow-red">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z"/></svg>
              </div>
              <span class="gl-flow-label">Redeems for discount</span>
            </div>
          </div>
        </div>

        <!-- ② Earning rate -->
        <div class="gl-section-card">
          <div class="gl-section-head">
            <div class="gl-section-icon gl-icon-teal">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
            </div>
            <div>
              <h2 class="gl-section-title">How Customers Earn Points</h2>
            </div>
          </div>

          <!-- Rate hero -->
          <div class="gl-rate-hero">
            <span class="gl-rate-num">{{ pointsPerRinggit }}</span>
            <div class="gl-rate-desc">
              <span class="gl-rate-word">point<span v-if="pointsPerRinggit !== 1">s</span> earned per</span>
              <span class="gl-rate-rm">RM 1 spent</span>
            </div>
          </div>

          <p class="gl-body-text">The base earning rate is multiplied by the customer's membership tier. A higher tier means more points per ringgit.</p>

          <!-- Worked examples -->
          <div class="gl-examples-head">
            <span class="gl-examples-label">Worked example — customer spending <strong>RM 40</strong></span>
          </div>
          <div v-if="tiers.length" class="gl-examples-grid">
            <div v-for="tier in tiers" :key="tier.id" class="gl-example-card">
              <div class="gl-ex-tier-badge" :style="tierBadgeStyle(tier)">{{ tier.name }}</div>
              <div class="gl-ex-formula">
                RM 40 × {{ pointsPerRinggit }} × <strong>{{ Number(tier.points_multiplier).toFixed(1) }}</strong>
              </div>
              <div class="gl-ex-result">= {{ exampleEarn(40, tier) }} pts</div>
            </div>
          </div>
          <p v-else class="gl-body-text gl-muted">No tiers configured yet.</p>
        </div>

        <!-- ③ Membership tiers -->
        <div class="gl-section-card">
          <div class="gl-section-head">
            <div class="gl-section-icon gl-icon-amber">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
            </div>
            <div>
              <h2 class="gl-section-title">Membership Tiers</h2>
              <p class="gl-section-sub">Customers are automatically placed in the right tier based on their lifetime points. Higher tiers earn points faster and unlock additional benefits.</p>
            </div>
          </div>

          <div v-if="!tiers.length" class="gl-empty-block">No tiers configured.</div>

          <div v-else class="gl-tiers-grid">
            <div v-for="tier in tiers" :key="tier.id" class="gl-tier-card" :style="{ '--tier-color': tierColor(tier) }">
              <div class="gl-tier-accent"></div>
              <div class="gl-tier-body">
                <div class="gl-tier-top">
                  <span class="gl-tier-name">{{ tier.name }}</span>
                  <span class="gl-tier-threshold">{{ tier.min_points.toLocaleString() }}+ pts</span>
                </div>
                <div class="gl-tier-multiplier">
                  <span class="gl-mult-num">{{ Number(tier.points_multiplier).toFixed(1) }}×</span>
                  <span class="gl-mult-lbl">earning multiplier</span>
                </div>
                <div class="gl-tier-divider"></div>
                <div class="gl-tier-benefits">
                  <span class="gl-benefits-lbl">Benefits</span>
                  <ul v-if="tier.benefits && tier.benefits.length" class="gl-benefits-list">
                    <li v-for="(b, i) in tier.benefits" :key="i">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                      {{ b }}
                    </li>
                  </ul>
                  <span v-else class="gl-no-benefits">No benefits listed.</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ④ Redemption -->
        <div class="gl-section-card">
          <div class="gl-section-head">
            <div class="gl-section-icon gl-icon-red">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z"/></svg>
            </div>
            <div>
              <h2 class="gl-section-title">Redeeming Points</h2>
            </div>
          </div>

          <div class="gl-redeem-metrics">
            <div class="gl-redeem-metric">
              <span class="gl-redeem-val">{{ pointsToRinggitRate }} pts</span>
              <span class="gl-redeem-eq">= RM 1 discount</span>
            </div>
            <div class="gl-redeem-sep"></div>
            <div class="gl-redeem-metric">
              <span class="gl-redeem-val">{{ minPointsRedeem }} pts</span>
              <span class="gl-redeem-eq">minimum to redeem</span>
            </div>
          </div>

          <p class="gl-body-text">Customers apply points at checkout. Once redeemed, points are deducted immediately and the equivalent ringgit value is discounted from the order total.</p>

          <div class="gl-example-block">
            <span class="gl-ex-block-label">Worked example</span>
            <div class="gl-ex-block-body">
              A customer with <strong>{{ exampleBalance }} points</strong> redeems all of them at checkout:
              <div class="gl-ex-block-calc">
                <code>{{ exampleBalance }} ÷ {{ pointsToRinggitRate }}</code>
                <span>= <strong class="gl-teal">RM {{ exampleRedeem }}</strong> off the order</span>
              </div>
            </div>
          </div>
        </div>

        <!-- ⑤ Expiry -->
        <div class="gl-section-card">
          <div class="gl-section-head">
            <div class="gl-section-icon gl-icon-sky">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            </div>
            <div>
              <h2 class="gl-section-title">Points Expiry</h2>
            </div>
          </div>
          <p class="gl-body-text">
            Earned points expire after <strong class="gl-teal">{{ pointsExpiryDays }} days</strong> from the date they were earned. Expired points are removed automatically and cannot be recovered.
          </p>
        </div>

        <!-- ⑥ Where to configure -->
        <div class="gl-section-card">
          <div class="gl-section-head">
            <div class="gl-section-icon gl-icon-gray">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>
            </div>
            <div>
              <h2 class="gl-section-title">Where to Configure</h2>
            </div>
          </div>
          <div class="gl-config-grid">
            <router-link to="/owner/settings" class="gl-config-link">
              <div class="gl-config-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></svg>
              </div>
              <div class="gl-config-text">
                <span class="gl-config-title">Loyalty Settings</span>
                <span class="gl-config-desc">Set earning rate, redemption rate, minimum redeem, and expiry days.</span>
              </div>
              <svg class="gl-config-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
            </router-link>
            <router-link to="/owner/customers" class="gl-config-link">
              <div class="gl-config-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
              </div>
              <div class="gl-config-text">
                <span class="gl-config-title">Customer Tiers</span>
                <span class="gl-config-desc">View and filter customers by their current loyalty tier.</span>
              </div>
              <svg class="gl-config-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
            </router-link>
          </div>
        </div>

        <!-- Notice banner -->
        <div class="gl-notice-banner">
          <div class="gl-notice-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          </div>
          <div class="gl-notice-body">
            <strong>Heads up —</strong> the automatic points-award-on-order and tier-promotion engine is still being rolled out.
            Settings can already be edited here, but some rules may not yet take effect at checkout until the loyalty engine ships.
            Manual tier editing and a rewards catalog are planned in the next phase.
          </div>
        </div>

      </template>
    </div>
  </div>
</template>

<script>
import { ownerLoyaltyGuideApi } from '@/services/ownerApi';

const TIER_COLORS = { silver: '#78909c', gold: '#c8961e', platinum: '#5c6bc0' };
const TIER_BG = { silver: '#f1f5f9', gold: '#fef9ec', platinum: '#f0f0ff' };
const TIER_TEXT = { silver: '#475569', gold: '#92400e', platinum: '#3730a3' };

export default {
  name: 'LoyaltyGuide',

  data() {
    return { loading: true, settings: {}, tiers: [] };
  },

  computed: {
    pointsPerRinggit()   { return Number(this.settings.points_per_ringgit ?? 1); },
    pointsToRinggitRate(){ return Number(this.settings.points_to_ringgit_rate ?? 100); },
    minPointsRedeem()    { return Number(this.settings.min_points_redeem ?? 100); },
    pointsExpiryDays()   { return Number(this.settings.points_expiry_days ?? 365); },
    exampleBalance()     { return this.minPointsRedeem * 5; },
    exampleRedeem()      { return (this.exampleBalance / this.pointsToRinggitRate).toFixed(2); }
  },

  mounted() { this.fetchData(); },

  methods: {
    async fetchData() {
      try {
        this.loading = true;
        const r = await ownerLoyaltyGuideApi.get();
        const { settings = [], tiers = [] } = r.data.data || {};
        const flat = {};
        settings.forEach(s => { flat[s.key] = s.value; });
        this.settings = flat;
        this.tiers = tiers.map(t => ({ ...t, benefits: this.parseBenefits(t.benefits) }));
      } catch (e) {
        console.error('Failed to load loyalty data:', e);
      } finally {
        this.loading = false;
      }
    },

    parseBenefits(b) {
      if (!b) return [];
      if (Array.isArray(b)) return b;
      if (typeof b === 'string') { try { const p = JSON.parse(b); return Array.isArray(p) ? p : []; } catch { return []; } }
      return [];
    },

    tierColor(tier) {
      const slug = (tier.slug || tier.name || '').toLowerCase();
      return TIER_COLORS[slug] || '#900C27';
    },

    tierBadgeStyle(tier) {
      const slug = (tier.slug || tier.name || '').toLowerCase();
      return {
        background: TIER_BG[slug] || '#dff0ec',
        color: TIER_TEXT[slug] || '#900C27'
      };
    },

    exampleEarn(amount, tier) {
      return Math.floor(amount * this.pointsPerRinggit * (Number(tier.points_multiplier) || 1));
    }
  }
};
</script>

<style scoped>
/* ── Page header ── */
.gl-loyalty-page { padding-bottom: 40px; }
.gl-page-header { display: flex; align-items: center; justify-content: space-between; padding: 20px 0 10px; }
.gl-breadcrumb { font-size: 12px; color: #9ca3af; margin-bottom: 4px; }
.gl-breadcrumb a { color: #9ca3af; text-decoration: none; }
.gl-breadcrumb a:hover { color: #374151; }
.gl-bc-sep { margin: 0 6px; }
.gl-page-title { font-size: 22px; font-weight: 700; color: #111827; margin: 0; letter-spacing: -0.3px; }

/* ── Section cards ── */
.gl-section-card {
  background: #fff; border: 1.5px solid #f0f1f3; border-radius: 16px;
  padding: 24px 28px; margin-bottom: 14px;
  animation: gl-fadein 0.3s ease;
}
.gl-section-head { display: flex; align-items: flex-start; gap: 14px; margin-bottom: 20px; }
.gl-section-icon {
  width: 40px; height: 40px; border-radius: 10px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  background: #dff0ec; color: #900C27;
}
.gl-icon-teal  { background: #dff0ec; color: #900C27; }
.gl-icon-amber { background: #fef3c7; color: #d97706; }
.gl-icon-red   { background: #fee2e2; color: #dc2626; }
.gl-icon-sky   { background: #e0f2fe; color: #0284c7; }
.gl-icon-gray  { background: #f3f4f6; color: #6b7280; }
.gl-section-title { font-size: 17px; font-weight: 700; color: #111827; margin: 0 0 2px; letter-spacing: -0.2px; }
.gl-section-sub { font-size: 13px; color: #6b7280; margin: 4px 0 0; line-height: 1.55; max-width: 680px; }
.gl-body-text { font-size: 13.5px; color: #6b7280; line-height: 1.6; margin: 0 0 14px; }
.gl-muted { color: #9ca3af; }
.gl-teal { color: #900C27; }

/* ── Flow diagram ── */
.gl-flow {
  display: flex; align-items: center; justify-content: center; gap: 0;
  background: #f8f9fa; border-radius: 14px; padding: 24px 20px; flex-wrap: wrap;
}
.gl-flow-step { display: flex; flex-direction: column; align-items: center; text-align: center; flex: 1; min-width: 110px; }
.gl-flow-icon {
  width: 54px; height: 54px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center; margin-bottom: 10px;
}
.gl-flow-blue   { background: #dbeafe; color: #2563eb; }
.gl-flow-teal   { background: #dff0ec; color: #900C27; }
.gl-flow-amber  { background: #fef3c7; color: #d97706; }
.gl-flow-red    { background: #fee2e2; color: #dc2626; }
.gl-flow-label  { font-size: 12.5px; font-weight: 600; color: #374151; line-height: 1.3; }
.gl-flow-arrow  { color: #d1d5db; flex-shrink: 0; padding: 0 6px; }

/* ── Rate hero ── */
.gl-rate-hero {
  display: flex; align-items: center; gap: 16px;
  background: linear-gradient(135deg, #e8f5f1 0%, #f4faf8 100%);
  border: 1.5px solid #c8e2da; border-radius: 14px;
  padding: 20px 28px; margin-bottom: 16px;
}
.gl-rate-num { font-size: 52px; font-weight: 900; color: #900C27; line-height: 1; letter-spacing: -2px; }
.gl-rate-desc { display: flex; flex-direction: column; gap: 2px; }
.gl-rate-word { font-size: 15px; color: #6b7280; }
.gl-rate-rm   { font-size: 20px; font-weight: 700; color: #111827; letter-spacing: -0.3px; }

/* ── Examples ── */
.gl-examples-head { margin: 16px 0 10px; }
.gl-examples-label { font-size: 13px; font-weight: 600; color: #374151; }
.gl-examples-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 12px; }
.gl-example-card {
  background: #fafafa; border: 1.5px solid #f0f1f3; border-radius: 12px;
  padding: 16px; display: flex; flex-direction: column; gap: 8px;
}
.gl-ex-tier-badge { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 700; width: fit-content; }
.gl-ex-formula { font-size: 12.5px; color: #9ca3af; }
.gl-ex-formula strong { color: #374151; }
.gl-ex-result { font-size: 18px; font-weight: 800; color: #900C27; letter-spacing: -0.3px; }

/* ── Tier cards ── */
.gl-tiers-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 14px; }
.gl-tier-card {
  background: #fff; border: 1.5px solid #f0f1f3; border-radius: 14px;
  overflow: hidden; position: relative;
  transition: box-shadow 0.15s;
}
.gl-tier-card:hover { box-shadow: 0 4px 16px rgba(0,0,0,0.06); }
.gl-tier-accent { height: 5px; background: var(--tier-color); }
.gl-tier-body { padding: 18px 20px; }
.gl-tier-top { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 14px; }
.gl-tier-name { font-size: 17px; font-weight: 800; color: var(--tier-color); letter-spacing: -0.3px; }
.gl-tier-threshold { font-size: 11.5px; color: #9ca3af; font-weight: 500; }
.gl-tier-multiplier { display: flex; align-items: baseline; gap: 6px; margin-bottom: 16px; }
.gl-mult-num { font-size: 32px; font-weight: 900; color: var(--tier-color); line-height: 1; letter-spacing: -1px; }
.gl-mult-lbl { font-size: 12px; color: #9ca3af; font-weight: 500; }
.gl-tier-divider { height: 1px; background: #f5f6f8; margin-bottom: 12px; }
.gl-benefits-lbl { font-size: 10.5px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.07em; color: #9ca3af; display: block; margin-bottom: 8px; }
.gl-benefits-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 5px; }
.gl-benefits-list li { display: flex; align-items: flex-start; gap: 7px; font-size: 12.5px; color: #374151; }
.gl-benefits-list li svg { color: #900C27; flex-shrink: 0; margin-top: 2px; }
.gl-no-benefits { font-size: 12px; color: #b0b7c3; }

/* ── Redemption metrics ── */
.gl-redeem-metrics {
  display: flex; align-items: stretch; gap: 0;
  background: #f8f9fa; border: 1.5px solid #f0f1f3; border-radius: 14px;
  overflow: hidden; margin-bottom: 16px;
}
.gl-redeem-metric { flex: 1; display: flex; flex-direction: column; align-items: center; padding: 20px; gap: 4px; }
.gl-redeem-sep { width: 1px; background: #f0f1f3; flex-shrink: 0; }
.gl-redeem-val { font-size: 26px; font-weight: 800; color: #900C27; letter-spacing: -0.5px; }
.gl-redeem-eq { font-size: 12.5px; color: #9ca3af; font-weight: 500; }

/* ── Example block ── */
.gl-example-block {
  background: #f8f9fa; border-left: 3px solid #900C27; border-radius: 0 10px 10px 0;
  padding: 14px 18px; margin-top: 6px;
}
.gl-ex-block-label { font-size: 10.5px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.07em; color: #9ca3af; display: block; margin-bottom: 6px; }
.gl-ex-block-body { font-size: 13.5px; color: #374151; line-height: 1.6; }
.gl-ex-block-calc { margin-top: 8px; display: flex; align-items: center; gap: 10px; }
.gl-ex-block-calc code { background: #fff; border: 1px solid #e8eaf0; padding: 3px 10px; border-radius: 6px; font-size: 13px; color: #374151; font-family: 'SF Mono', 'Fira Code', monospace; }

/* ── Config links ── */
.gl-config-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 12px; }
.gl-config-link {
  display: flex; align-items: center; gap: 14px;
  padding: 16px 18px; border: 1.5px solid #e8eaf0; border-radius: 12px;
  background: #fafafa; text-decoration: none; color: inherit; transition: all 0.15s;
}
.gl-config-link:hover { border-color: #900C27; background: #f0f5f4; color: inherit; }
.gl-config-link:hover .gl-config-arrow { color: #900C27; transform: translateX(3px); }
.gl-config-icon {
  width: 38px; height: 38px; border-radius: 9px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  background: #dff0ec; color: #900C27;
}
.gl-config-text { flex: 1; display: flex; flex-direction: column; gap: 2px; }
.gl-config-title { font-size: 13.5px; font-weight: 700; color: #111827; }
.gl-config-desc { font-size: 12px; color: #9ca3af; line-height: 1.4; }
.gl-config-arrow { color: #d1d5db; flex-shrink: 0; transition: all 0.15s; }

/* ── Notice banner ── */
.gl-notice-banner {
  display: flex; align-items: flex-start; gap: 12px;
  background: #fffbf0; border: 1.5px solid #fde68a; border-radius: 12px;
  padding: 14px 18px; margin-bottom: 14px;
}
.gl-notice-icon { color: #d97706; flex-shrink: 0; margin-top: 1px; }
.gl-notice-body { font-size: 13px; color: #92400e; line-height: 1.6; }
.gl-notice-body strong { color: #78350f; }

/* ── Empty + skeleton ── */
.gl-empty-block { text-align: center; padding: 32px; color: #9ca3af; font-size: 13.5px; }
.gl-skel-page { display: flex; flex-direction: column; gap: 14px; }
.gl-skel-card-block { background: #f0f1f3; border-radius: 16px; animation: gl-pulse 1.4s ease-in-out infinite; }

/* ── Mobile ── */
@media (max-width: 767px) {
  .gl-page-header { padding: 14px 0 8px; }
  .gl-page-title { font-size: 18px; }
  .gl-breadcrumb { display: none; }

  .gl-section-card { padding: 16px; }
  .gl-section-head { gap: 10px; margin-bottom: 14px; }
  .gl-section-icon { width: 34px; height: 34px; border-radius: 9px; flex-shrink: 0; }
  .gl-section-title { font-size: 15px; }
  .gl-section-sub { font-size: 12.5px; }

  /* Flow: hide arrows, wrap steps into 2×2 grid */
  .gl-flow { padding: 16px 12px; gap: 12px; justify-content: space-around; }
  .gl-flow-step { min-width: 80px; flex: 0 0 calc(50% - 16px); }
  .gl-flow-arrow { display: none; }
  .gl-flow-icon { width: 44px; height: 44px; }

  /* Rate hero */
  .gl-rate-hero { padding: 16px; gap: 12px; }
  .gl-rate-num { font-size: 40px; }
  .gl-rate-rm { font-size: 17px; }

  /* Tier grid: 1 column */
  .gl-tiers-grid { grid-template-columns: 1fr; }

  /* Redemption metrics: keep side by side but tighter */
  .gl-redeem-metric { padding: 14px 10px; }
  .gl-redeem-val { font-size: 22px; }

  /* Example calc: wrap */
  .gl-ex-block-calc { flex-wrap: wrap; gap: 6px; }

  /* Config links: stack */
  .gl-config-grid { grid-template-columns: 1fr; }

  /* Examples grid: 1 col on very narrow */
  .gl-examples-grid { grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); }
}

/* ── Animations ── */
@keyframes gl-fadein { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
@keyframes gl-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
</style>

<style>
/* Dark mode */
body.dark-only .gl-page-title { color: #f3f4f6; }
body.dark-only .gl-breadcrumb, body.dark-only .gl-breadcrumb a { color: #6b7280; }

body.dark-only .gl-section-card { background: #1f2937; border-color: #374151; }
body.dark-only .gl-section-title { color: #f9fafb; }
body.dark-only .gl-section-sub, body.dark-only .gl-body-text { color: #9ca3af; }
body.dark-only .gl-flow { background: #111827; }
body.dark-only .gl-flow-label { color: #d1d5db; }
body.dark-only .gl-flow-arrow { color: #4b5563; }

body.dark-only .gl-rate-hero { background: linear-gradient(135deg, #1a3330 0%, #1f2937 100%); border-color: #2d5249; }
body.dark-only .gl-rate-word { color: #9ca3af; }
body.dark-only .gl-rate-rm { color: #f9fafb; }

body.dark-only .gl-example-card { background: #111827; border-color: #374151; }
body.dark-only .gl-ex-formula { color: #6b7280; }
body.dark-only .gl-ex-formula strong { color: #d1d5db; }
body.dark-only .gl-examples-label { color: #d1d5db; }

body.dark-only .gl-tier-card { background: #1f2937; border-color: #374151; }
body.dark-only .gl-tier-threshold { color: #6b7280; }
body.dark-only .gl-mult-lbl { color: #6b7280; }
body.dark-only .gl-tier-divider { background: #374151; }
body.dark-only .gl-benefits-list li { color: #d1d5db; }
body.dark-only .gl-no-benefits { color: #4b5563; }

body.dark-only .gl-redeem-metrics { background: #111827; border-color: #374151; }
body.dark-only .gl-redeem-sep { background: #374151; }
body.dark-only .gl-redeem-eq { color: #6b7280; }

body.dark-only .gl-example-block { background: #111827; }
body.dark-only .gl-ex-block-body { color: #d1d5db; }
body.dark-only .gl-ex-block-calc code { background: #1f2937; border-color: #374151; color: #d1d5db; }

body.dark-only .gl-config-link { background: #111827; border-color: #374151; }
body.dark-only .gl-config-link:hover { border-color: #900C27; background: #1a3330; }
body.dark-only .gl-config-title { color: #f9fafb; }
body.dark-only .gl-config-desc { color: #6b7280; }

body.dark-only .gl-notice-banner { background: #2a1f08; border-color: #92400e; }
body.dark-only .gl-notice-body { color: #fde68a; }
body.dark-only .gl-notice-body strong { color: #fef3c7; }

body.dark-only .gl-skel-card-block { background: #374151; }
</style>
